import { Box, Typography } from "@mui/material";
import { Col, Form, Modal, Row, Switch, message } from "antd";
import { FormInstance } from "antd/lib";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ActionButton from "../common/buttons/ActionButton";
import CancelButton from "../common/buttons/CancelButton";
import InputField from "../common/fields/InputField";
import { SystemConfigsRequest, SystemConfigsResponse } from "../../@type/SystemConfigs.types";
import { SystemConfigsService } from "../../api/systemConfigsService";

interface SystemConfigsFormValues {
  configKey: string;
  configValue: string;
  description?: string;
  status: boolean;
}

interface AddSystemConfigsModalProps {
  open: boolean;
  onCancel: () => void;
  reloadData: () => void;
  editData?: SystemConfigsResponse | null;
}

const AddSystemConfigsModal: React.FC<AddSystemConfigsModalProps> = ({
  open,
  onCancel,
  reloadData,
  editData,
}) => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance<SystemConfigsFormValues>>(null);
  const [loading, setLoading] = useState(false);
  const isEdit = Boolean(editData);

  // Populate form values when the modal opens
  useEffect(() => {
    if (!open) {
      return;
    }
    formRef.current?.resetFields();
    if (editData) {
      formRef.current?.setFieldsValue({
        configKey: editData.config_key,
        configValue: editData.config_value,
        description: editData.description,
        status: editData.status === 1,
      });
    } else {
      formRef.current?.setFieldsValue({ status: true });
    }
  }, [open, editData]);

  const onFinish = (values: SystemConfigsFormValues) => {
    setLoading(true);
    // Jackson SNAKE_CASE on the server: request body keys must be snake_case
    const payload: SystemConfigsRequest = {
      config_key: values.configKey,
      config_value: values.configValue,
      description: values.description ?? "",
      status: values.status ? 1 : 0,
    };
    const request = isEdit
      ? SystemConfigsService.update(editData!.id, payload)
      : SystemConfigsService.create(payload);

    request
      .then(() => {
        message.success(
          isEdit
            ? t("system_config.form.updated_ok")
            : t("system_config.form.added_ok")
        );
        onCancel();
        reloadData();
      })
      .catch(() => {
        message.error(
          isEdit
            ? t("system_config.form.update_error")
            : t("system_config.form.add_error")
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal open={open} footer={null} onCancel={onCancel} width={600} destroyOnClose>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {isEdit ? t("system_config.form.edit_title") : t("system_config.form.add_title")}
      </Typography>
      <Form
        onFinish={onFinish}
        ref={formRef}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign="left"
        initialValues={{ status: true }}
        preserve={false}
      >
        <Row>
          <Col span={24}>
            <Form.Item
              name="configKey"
              label={t("system_config.form.key")}
              rules={[{ required: true, message: t("system_config.form.key_required") }]}
            >
              <InputField disabled={isEdit} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="configValue"
              label={t("system_config.form.value")}
              rules={[{ required: true, message: t("system_config.form.value_required") }]}
            >
              <InputField multiline minRows={4} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="description" label={t("system_config.form.description")}>
              <InputField />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="status"
              label={t("system_config.form.status")}
              rules={[{ required: true, message: t("system_config.form.status_required") }]}
              valuePropName="checked"
            >
              <Switch checkedChildren={t("common.switch.on")} unCheckedChildren={t("common.switch.off")} />
            </Form.Item>
          </Col>
        </Row>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <CancelButton onClick={onCancel}>{t("btn.cancel")}</CancelButton>
          <ActionButton type="submit" loading={loading}>
            {isEdit ? t("system_config.form.update") : t("system_config.form.add")}
          </ActionButton>
        </Box>
      </Form>
    </Modal>
  );
};

export default AddSystemConfigsModal;
